
const av = Autodesk.Viewing;
const avel = AutodeskNamespace('Autodesk.AEC.Locations');

function _export(m, ns) {
    for (let prop in m) {
        if (Object.prototype.hasOwnProperty.call(m, prop)) {
            //Export directly into the module (e.g. for node.js use, where LMV is used via require instead from global namespace)
            module.exports[prop] = m[prop];

            //Export into the desired viewer namespace
            ns[prop] = m[prop];
        }
    }
}

_export(require("./LocationsService.js"), avel);
_export(require("./LocationsCommon.js"), avel);

const LOCATIONS_2D_CONTEXT_NAME = 'locations-2d-context';
const FILL_ALPHA = 0.2;
const FILL_ALPHA_HOVER = 0.5;

const POLYGON_STYLE = {
    fillColor: "rgb(205, 234, 247)",
    fillAlpha: FILL_ALPHA,
    lineColor: "rgb(0, 110, 175)",
    lineWidth: 2
};

const LABEL_CONTAINER_STYLE = {
    background: "#FFFFFF",
    borderRadius: "17px",
    display: "flex",
    flexDirection: "row",
    padding: "5px 9px",
    boxShadow: "none",
    zIndex: 0,
};

const LABEL_STYLE = {
  color: "#006EAF",
  fontSize: "12px",
  lineHeight: "12px",
  alignSelf: "center",
};

const LABEL_SHADOW = "0px 4px 14px rgba(12, 44, 84, 0.15), 0px 0px 2px rgba(12, 44, 84, 0.25)";

/**
 * 2D locations library extension
 */
class LocationsExtension extends av.Extension {
  constructor(viewer, options) {
    super(viewer, options);

    this.onModelAdded = this.onModelAdded.bind(this);
  }

  async load() {
    this.locations = [];

    this.edit2dExtension = await this.viewer.loadExtension("Autodesk.Edit2D");

    this.edit2dContext = this.edit2dExtension.registerTools(
      LOCATIONS_2D_CONTEXT_NAME
    );

    // Class definition has to be located here, only after loading Edit2D, since it extends a member of the namespace.
    class LocationLabel extends Autodesk.Edit2D.FadeOutStyleRule {
      apply(label, shape, layer) {
        super.apply(label, shape, layer);
        if (!label.styleInitialized) {
          label.styleInitialized = true;
          Object.assign(label.container.style, LABEL_CONTAINER_STYLE);
          Object.assign(label.textDiv.style, LABEL_STYLE);
        }

        if (label.hoverStyle ^ shape.hover) {
          label.hoverStyle = !!shape.hover;
          label.container.style.boxShadow = shape.hover
          ? LABEL_SHADOW
          : "none";
        }
      }
    }

    this.shapeLabelRule = new Autodesk.Edit2D.ShapeLabelRule(
      this.edit2dContext.layer,
      (shape) => shape.name,
      undefined,
      new LocationLabel()
    );

    this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);

    this.onModelAdded();

    return true;
  }

  unload() {
    this.edit2dExtension && this.edit2dExtension.unregisterTools(LOCATIONS_2D_CONTEXT_NAME);

    this.shapeLabelRule.dtor();
    this.shapeLabelRule = null;
    this.edit2dExtension = null;
    this.edit2dContext = null;

    this.locations = [];

    this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);

    return true;
  }

  onModelAdded() {
    const model = this.viewer.model;
    if (!model) {
      return;
    }

    // Since Leaflet coordinates are normalized, and locations are being saved in page coordinates,
    // we need to set transformation for the locations layer - so it will be displayed correctly.
    if (model.isPageCoordinates()) {
      return;
    }

    const normalize = Autodesk.Viewing.PDFUtils.getLeafletNormalizingMatrix(this.viewer);

    if (!normalize) {
      return;
    }

    const pageDimensions = model.getMetadata("page_dimensions");
    const scale = new THREE.Matrix4();
    scale.makeScale(pageDimensions.page_width, pageDimensions.page_height, 1);

    const layerToWorld = new THREE.Matrix4();
    layerToWorld.multiplyMatrices(scale, normalize);
    layerToWorld.invert();

    this.edit2dContext.setMatrix(layerToWorld);
  }

  /**
   * Set locations array.
   */
  setLocations(locations) {
    this.locations = locations;

    // In case showAllLocations was called before setLocations (because of slow network for example),
    // Make sure to call it again, this time with the locations data.
    if (this.allLocationsVisible) {
      this.showAllLocations();
    }
  }

  /**
   * Get locations array.
   */
  getLocations() {
    return this.locations;
  }

  /**
   * Get location item by id.
   */
  getLocationById(id) {
    const location = this.locations.find((item) => item.id === id);

    if (!location) {
      console.warn("Cant find location: ", id);
    }

    return location;
  }

  /**
   * Show location by id.
   */
  showLocationById(id) {
    const location = this.getLocationById(id);
    this.showLocationItem(location);
  }

  /**
   * Hide location by id.
   */
  hideLocationById(id) {
    const location = this.getLocationById(id);
    this.hideLocationItem(location);
  }

  /**
   * Show location item.
   */
  showLocationItem(location) {
    if (location) {
      const polygon = location.polygon;

      // Update polygon style
      Object.assign(polygon.style, POLYGON_STYLE);

      if (!this.edit2dContext.layer.findShapeById(polygon.id)) {
        this.edit2dContext.layer.addShape(polygon);
      }
    }
  }

  /**
   * Hide location item.
   */
  hideLocationItem(location) {
    if (location) {
      const polygon = location.polygon;
      this.edit2dContext.layer.removeShape(polygon);
    }
  }

  /**
   * Show all locations.
   */
  showAllLocations() {
    // Disable autoUpdate for this batch operation.
    // Call update only onces at the end.
    this.edit2dContext.layer.autoUpdate = false;

    this.locations.forEach((location) => {
      this.showLocationItem(location);
    });

    this.edit2dContext.layer.autoUpdate = true;
    this.edit2dContext.layer.update();

    this.allLocationsVisible = true;
  }

  /**
   * Hide all locations.
   */
  hideAllLocations() {
    // Disable autoUpdate for this batch operation.
    // Call update only onces at the end.
    this.edit2dContext.layer.autoUpdate = false;

    this.locations.forEach((location) => {
      this.hideLocationItem(location);
    });

    this.edit2dContext.layer.autoUpdate = true;
    this.edit2dContext.layer.update();

    this.allLocationsVisible = false;
  }

  getLocationIdsAtPoint(point) {
    const intersectedLocations = Autodesk.AEC.Locations.getPointLocations(
      point,
      this.locations
    );

    return intersectedLocations.map((location) => location.id);
  }

  onMouseMove(event) {
    var rect = this.viewer.impl.getCanvasBoundingClientRect();

    let point = this.viewer.clientToWorld(
      event.clientX - rect.left,
      event.clientY - rect.top,
      undefined,
      true
    ).point;

    if (!this.viewer.model.isPageCoordinates()) {
      // Locations are stored using page coordinate system.
      // In case we are viewing a Leaflet document, the point position has to be converted into the same coordinate system.
      point = Autodesk.Viewing.PDFUtils.leafletToPdfWorld(this.viewer, point);

      if (!point) {
        return;
      }
    }

    const locationIds = this.getLocationIdsAtPoint(point);

    // Hover only first location if available.
    const singleLocationIdArray = locationIds.slice(0, 1);

    this.hoverLocationsByIds(singleLocationIdArray);
  }

  /**
   * Hover locations according to ids.
   */
  hoverLocationsByIds(ids) {
    let dirty = false;

    this.locations.forEach((location) => {
      const polygon = location.polygon;
      if (ids.indexOf(location.id) !== -1) {
        if (!polygon.hover) {
          polygon.style.fillAlpha = FILL_ALPHA_HOVER;
          polygon.hover = true;
          dirty = true;
        }
      } else {
        if (polygon.hover) {
          polygon.style.fillAlpha = FILL_ALPHA;
          polygon.hover = false;
          dirty = true;
        }
      }
    });

    if (dirty) {
      this.edit2dContext.layer.update();
    }
  }
}

// The ExtensionManager requires an extension to be registered.
av.theExtensionManager.registerExtension('Autodesk.AEC.LocationsExtension', LocationsExtension);
